import ApartmentOutlinedIcon from '@mui/icons-material/ApartmentOutlined';
import AssignmentIndOutlinedIcon from '@mui/icons-material/AssignmentIndOutlined';
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import CheckIcon from '@mui/icons-material/Check';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Box, ButtonBase, Card, CardHeader, Grid2 as Grid, LinearProgress, ListItemIcon, ListItemText, Menu, MenuItem, Stack, Typography } from '@mui/material';
import { tr, uiLocale } from '../lib/i18n';
import { useTheme } from '@mui/material/styles';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { api } from '../api/client';
import type { StatsOverview } from '../api/types';
import { useAuth } from '../auth/AuthContext';
import { PersonAvatar } from '../components/AccountMenu';
import { Button } from '../components/button';
import { BarsCard, dayRange, RingGauge, TONE, TrendChart } from '../components/charts';
import { Empty, ErrorState } from '../components/common';
import { DataGrid, type GridColDef } from '../components/data-grid';
import { PageActions } from '../components/page-actions';
import { DashboardSkeleton } from '../components/skeletons';
import { TextField } from '../components/text-field';
import { apiLabel, fmtNum, PROPERTY_STATUS, REQUEST_STATUS, SOURCE } from '../lib/labels';

const iso = (d: Date) => d.toISOString().slice(0, 10);
const rangeOf = (days: number) => ({ from: iso(new Date(Date.now() - (days - 1) * 86400_000)), to: iso(new Date()) });
const PRESETS = [
  { value: '7', label: tr('7 أيام', '7 days'), long: tr('آخر 7 أيام', 'Last 7 days') },
  { value: '30', label: tr('30 يوماً', '30 days'), long: tr('آخر 30 يوماً', 'Last 30 days') },
  { value: '90', label: tr('90 يوماً', '90 days'), long: tr('آخر 90 يوماً', 'Last 90 days') },
  { value: '365', label: tr('سنة', '1 year'), long: tr('آخر سنة', 'Last year') },
  { value: 'custom', label: tr('مخصص', 'Custom'), long: tr('فترة مخصصة', 'Custom period') },
] as const;
type Preset = (typeof PRESETS)[number]['value'];

const greeting = () => {
  const h = new Date().getHours();
  return h < 12 ? tr('صباح الخير', 'Good morning') : tr('مساء الخير', 'Good evening');
};
const fmtLong = (d: string) => new Date(`${d}T00:00:00`).toLocaleDateString(uiLocale(), { day: 'numeric', month: 'long' });

export function DashboardPage() {
  const { can, me } = useAuth();
  const m3 = useTheme().palette.m3;
  const [range, setRange] = useState(rangeOf(30));
  const [preset, setPreset] = useState<Preset>('30');
  const [periodAnchor, setPeriodAnchor] = useState<HTMLElement | null>(null);
  const [metric, setMetric] = useState<'PROPERTY' | 'REQUEST'>('REQUEST');
  const stats = useQuery({ queryKey: ['stats', range], queryFn: () => api<StatsOverview>('/stats/overview', { query: range }), placeholderData: keepPreviousData });
  const s = stats.data;
  const days = s ? dayRange(s.range.from, s.range.to) : [];
  const perDay = (entity: string) => {
    const m = new Map(s?.series.filter((r) => r.entity === entity).map((r) => [r.day, r.total]));
    return days.map((d) => m.get(d) ?? 0);
  };
  const of = <T extends { entity: string }>(rows: T[] | undefined, e: string) => (rows ?? []).filter((r) => r.entity === e);
  const merge = (rows: { label: string; value: number; color?: string }[]) =>
    rows.reduce<{ label: string; value: number; color?: string }[]>((acc, r) => {
      const e = acc.find((x) => x.label === r.label);
      if (e) e.value += r.value;
      else acc.push({ ...r });
      return acc;
    }, []);
  const presetLabel = PRESETS.find((p) => p.value === preset)!.long;

  return (
    <>
      <PageActions>
        {preset === 'custom' && (
          <>
            <TextField size="small" type="date" label={tr('من', 'From')} value={range.from} onChange={(e) => setRange({ ...range, from: e.target.value })} sx={{ width: 160 }} />
            <TextField size="small" type="date" label={tr('إلى', 'To')} value={range.to} onChange={(e) => setRange({ ...range, to: e.target.value })} sx={{ width: 160 }} />
          </>
        )}
        <Button
          size="small"
          variant="outlined"
          startIcon={<CalendarMonthOutlinedIcon />}
          endIcon={<ExpandMoreIcon />}
          onClick={(e) => setPeriodAnchor(e.currentTarget)}
          aria-haspopup="menu"
          aria-expanded={!!periodAnchor}
          sx={{ bgcolor: 'background.paper', minWidth: 150, justifyContent: 'space-between' }}
        >
          {PRESETS.find((p) => p.value === preset)?.label}
        </Button>
        <Menu anchorEl={periodAnchor} open={!!periodAnchor} onClose={() => setPeriodAnchor(null)} slotProps={{ paper: { sx: { minWidth: 200 } } }}>
          {PRESETS.map((p) => (
            <MenuItem
              key={p.value}
              selected={p.value === preset}
              onClick={() => {
                setPreset(p.value);
                if (p.value !== 'custom') setRange(rangeOf(Number(p.value)));
                setPeriodAnchor(null);
              }}
            >
              <ListItemIcon>{p.value === preset ? <CheckIcon color="primary" /> : null}</ListItemIcon>
              <ListItemText primary={p.long} />
            </MenuItem>
          ))}
        </Menu>
      </PageActions>

      <Box sx={{ mb: 2.5 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          {greeting()}{tr('،', ',')} {me?.user.full_name?.split(' ')[0]}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          {s ? tr(`ملخص ${presetLabel} — من ${fmtLong(s.range.from)} إلى ${fmtLong(s.range.to)}`, `${presetLabel} summary — ${fmtLong(s.range.from)} to ${fmtLong(s.range.to)}`) : tr('ملخص نشاط الشركة', 'Organization activity summary')}
        </Typography>
      </Box>

      {stats.isLoading && !s ? <DashboardSkeleton /> : stats.isError ? <ErrorState error={stats.error} onRetry={() => stats.refetch()} /> : s && (
        <Box sx={{ opacity: stats.isFetching ? 0.65 : 1, transition: 'opacity 200ms' }}>
          <Grid container spacing={2}>
            {/* overview: metric tabs on top of the trend (Analytics-style) */}
            <Grid size={{ xs: 12, lg: 8 }}>
              <Card sx={{ height: '100%' }}>
                <Stack direction="row" sx={{ borderBottom: 1, borderColor: 'divider' }}>
                  <MetricTab
                    active={metric === 'REQUEST'}
                    onClick={() => setMetric('REQUEST')}
                    icon={<AssignmentOutlinedIcon />}
                    label={tr('طلبات جديدة', 'New requests')}
                    value={fmtNum(s.totals.requests)}
                  />
                  <MetricTab
                    active={metric === 'PROPERTY'}
                    onClick={() => setMetric('PROPERTY')}
                    icon={<ApartmentOutlinedIcon />}
                    label={tr('عقارات جديدة', 'New properties')}
                    value={fmtNum(s.totals.properties)}
                  />
                </Stack>
                <Box sx={{ px: 1, pt: 1, pb: 0.5 }}>
                  <TrendChart
                    days={days}
                    data={perDay(metric)}
                    label={metric === 'REQUEST' ? tr('الطلبات', 'Requests') : tr('العقارات', 'Properties')}
                    color={metric === 'REQUEST' ? m3.primary : TONE.teal}
                    height={280}
                  />
                </Box>
              </Card>
            </Grid>

            <Grid size={{ xs: 12, lg: 4 }}>
              <Stack spacing={2} sx={{ height: '100%' }}>
                <Card sx={{ flex: 1 }}>
                  <Stack direction="row" alignItems="flex-start" spacing={2} sx={{ p: 2.5 }}>
                    <IconTile color={s.live.unassignedRequests ? TONE.wait : TONE.good}><AssignmentIndOutlinedIcon /></IconTile>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body2" color="text.secondary">{tr('طلبات بانتظار التوزيع', 'Requests awaiting assignment')}</Typography>
                      <Typography sx={{ fontSize: '2rem', lineHeight: 1.25, mt: 0.25 }}>{fmtNum(s.live.unassignedRequests)}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {s.live.unassignedRequests ? tr('طلبات لم يُعيَّن لها موظف بعد', 'Requests have not been assigned yet') : tr('كل الطلبات موزّعة على الفريق', 'All requests are assigned to the team')}
                      </Typography>
                    </Box>
                  </Stack>
                  {s.live.unassignedRequests > 0 && can('requests.assign') && (
                    <Box sx={{ px: 2.5, pb: 2.5 }}>
                      <Button variant="tonal" size="small" component={RouterLink} to="/requests?assignedTo=unassigned">{tr('توزيع الآن', 'Assign now')}</Button>
                    </Box>
                  )}
                </Card>
                <Card sx={{ flex: 1 }}>
                  <Stack direction="row" alignItems="center" spacing={2} sx={{ p: 2.5 }}>
                    <Box sx={{ flex: 1 }}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <CheckCircleOutlineIcon sx={{ color: TONE.good, fontSize: 20 }} />
                        <Typography variant="body2" color="text.secondary">{tr('نسبة النجاح', 'Success rate')}</Typography>
                      </Stack>
                      <Typography variant="caption" color="text.secondary" component="p" sx={{ mt: 1 }}>
                        {tr('من الطلبات المغلقة في الفترة: ناجحة مقابل خاسرة.', 'Won versus lost requests closed during this period.')}
                      </Typography>
                    </Box>
                    <RingGauge value={s.conversionRate} size={112} color={TONE.good} />
                  </Stack>
                </Card>
              </Stack>
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <BarsCard title={tr('الطلبات حسب الحالة', 'Requests by status')} rows={merge(of(s.byStatus, 'REQUEST').map((r) => ({ label: REQUEST_STATUS[r.status] ?? r.status, value: r.total })))} />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <BarsCard title={tr('العقارات حسب الحالة', 'Properties by status')} rows={merge(of(s.byStatus, 'PROPERTY').map((r) => ({ label: PROPERTY_STATUS[r.status] ?? r.status, value: r.total })))} />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <BarsCard title={tr('حسب نوع الطلب', 'By request type')} rows={merge(s.byRequestType.map((r) => ({ label: apiLabel(r.name_ar) ?? r.name_ar, value: r.total })))} />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <BarsCard
                title={tr('حسب المصدر', 'By source')}
                rows={merge(s.bySource.map((r) => ({
                  label: `${SOURCE[r.source] ?? r.source} (${r.entity === 'PROPERTY' ? tr('عقارات', 'Properties') : tr('طلبات', 'Requests')})`,
                  value: r.total,
                })))}
              />
            </Grid>
            <Grid size={12}><EmployeesCard rows={s.employees} /></Grid>
          </Grid>
        </Box>
      )}
    </>
  );
}

function IconTile({ color, children }: { color: string; children: ReactNode }) {
  return (
    <Box sx={{ width: 44, height: 44, borderRadius: '12px', display: 'grid', placeItems: 'center', flexShrink: 0, bgcolor: `${color}1F`, color, '& svg': { fontSize: 24 } }}>
      {children}
    </Box>
  );
}

function MetricTab({ active, onClick, icon, label, value }: { active: boolean; onClick: () => void; icon: ReactNode; label: string; value: ReactNode }) {
  const m3 = useTheme().palette.m3;
  return (
    <ButtonBase
      onClick={onClick}
      aria-pressed={active}
      sx={{
        flex: 1,
        justifyContent: 'flex-start',
        textAlign: 'start',
        px: 2.5,
        pt: 2,
        pb: 1.75,
        position: 'relative',
        bgcolor: active ? 'transparent' : m3.surfaceContainerLow,
        transition: 'background-color 150ms',
        '&:hover': { bgcolor: active ? 'transparent' : m3.surfaceContainer },
        '&::after': {
          content: '""',
          position: 'absolute',
          insetInline: 0,
          top: 0,
          height: 3,
          bgcolor: active ? m3.primary : 'transparent',
        },
        '&:first-of-type': { borderStartStartRadius: 16 },
        '&:last-of-type': { borderStartEndRadius: 16 },
        '& + &': { borderInlineStart: 1, borderColor: 'divider' },
      }}
    >
      <Box>
        <Stack direction="row" spacing={0.75} alignItems="center" sx={{ color: active ? m3.primary : m3.onSurfaceVariant, '& svg': { fontSize: 18 } }}>
          {icon}
          <Typography variant="body2" fontWeight={500}>{label}</Typography>
        </Stack>
        <Typography sx={{ fontSize: { xs: '1.75rem', md: '2.25rem' }, lineHeight: 1.25, mt: 0.5, color: 'text.primary' }}>{value}</Typography>
      </Box>
    </ButtonBase>
  );
}

function EmployeesCard({ rows }: { rows: StatsOverview['employees'] }) {
  const columns: GridColDef<(typeof rows)[number] & { rate: number }>[] = [
    {
      field: 'full_name',
      headerName: tr('الموظف', 'Employee'),
      flex: 1,
      minWidth: 200,
      renderCell: ({ row }) => (
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ height: '100%' }}>
          <PersonAvatar name={row.full_name} seed={row.id} size={30} />
          <Typography variant="body2" fontWeight={500} noWrap>{row.full_name}</Typography>
        </Stack>
      ),
    },
    { field: 'assigned', headerName: tr('معيّنة', 'Assigned'), type: 'number', width: 100 },
    { field: 'open', headerName: tr('مفتوحة', 'Open'), type: 'number', width: 100 },
    { field: 'won', headerName: tr('ناجحة', 'Won'), type: 'number', width: 100 },
    { field: 'lost', headerName: tr('خاسرة', 'Lost'), type: 'number', width: 100 },
    {
      field: 'rate',
      headerName: tr('نسبة النجاح', 'Success rate'),
      flex: 1,
      minWidth: 200,
      type: 'number',
      valueFormatter: (v) => `${v}%`,
      renderCell: ({ row }) => {
        const closed = row.won + row.lost;
        return closed ? (
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ height: '100%', width: '100%' }}>
            <LinearProgress
              variant="determinate"
              value={row.rate}
              sx={{ flex: 1, height: 8, '& .MuiLinearProgress-bar': { bgcolor: row.rate >= 50 ? TONE.good : TONE.wait } }}
            />
            <Typography variant="body2" sx={{ minWidth: 40, textAlign: 'end' }}>{row.rate}%</Typography>
          </Stack>
        ) : <Typography variant="caption" color="text.secondary">{tr('لا طلبات مغلقة', 'No closed requests')}</Typography>;
      },
    },
  ];
  const gridRows = rows.map((r) => {
    const closed = r.won + r.lost;
    return { ...r, rate: closed ? Math.round((r.won / closed) * 100) : 0 };
  });
  return (
    <Card>
      <CardHeader title={tr('أداء الموظفين', 'Employee performance')} subheader={tr('الطلبات المعيّنة لكل موظف خلال الفترة', 'Requests assigned to each employee during this period')} subheaderTypographyProps={{ variant: 'caption' }} />
      {rows.length === 0 ? <Empty text={tr('لا توجد طلبات معيّنة في هذه الفترة', 'No assigned requests during this period')} /> : (
        <DataGrid
          rows={gridRows}
          columns={columns}
          showToolbar={false}
          rowHeight={56}
          height={Math.max(260, Math.min(480, 120 + rows.length * 56))}
        />
      )}
    </Card>
  );
}
